/**
 * Calendar 유틸리티 함수들
 * 
 * Calendar.jsx에서 분리된 순수 함수들입니다.
 * 이벤트 변환, 시간 처리, 제한 체크 등의 로직을 담당합니다.
 */

import { DateTime } from 'luxon';
import { CALENDAR_DISPLAY } from '../../../constants/calendarDisplay';
import { api as apiClient } from '../../../services/api';

export const DEFAULT_TIMEZONE = 'Europe/Paris';

const RESTRICTED_PERIOD_CREATION_MODES = new Set(['normal', 'add', 'bidon']);

const DEFAULT_OVERLAP_OPTIONS = {
    excludeAllDay: true,
};

const SUPPORTED_MESSAGE_LANGUAGES = new Set(['ko', 'en', 'fr']);

const STUDIO_UNAVAILABLE_MESSAGES = {
    ko: {
        timed: ({ date, start, end }) => `${date}은 ${start}부터 ${end}까지 스튜디오 사용이 불가능합니다.`,
        crossDay: ({ startDate, start, endDate, end }) => `${startDate} ${start}부터 ${endDate} ${end}까지 스튜디오 사용이 불가능합니다.`,
        allDay: ({ date }) => `${date}은 하루 종일 스튜디오 사용이 불가능합니다.`,
        allDayRange: ({ startDate, endDate }) => `${startDate}부터 ${endDate}까지 하루 종일 스튜디오 사용이 불가능합니다.`,
        multiple: (ranges) => `다음 시간에는 스튜디오 사용이 불가능합니다:\n${ranges.map(range => `• ${range}`).join('\n')}`,
        range: {
            timed: ({ date, start, end }) => `${date} ${start}–${end}`,
            crossDay: ({ startDate, start, endDate, end }) => `${startDate} ${start}–${endDate} ${end}`,
            allDay: ({ date }) => `${date} 하루 종일`,
            allDayRange: ({ startDate, endDate }) => `${startDate}–${endDate} 하루 종일`,
        },
    },
    en: {
        timed: ({ date, start, end }) => `The studio is unavailable on ${date} from ${start} to ${end}.`,
        crossDay: ({ startDate, start, endDate, end }) => `The studio is unavailable from ${startDate} ${start} to ${endDate} ${end}.`,
        allDay: ({ date }) => `The studio is unavailable all day on ${date}.`,
        allDayRange: ({ startDate, endDate }) => `The studio is unavailable all day from ${startDate} through ${endDate}.`,
        multiple: (ranges) => `The studio is unavailable during these times:\n${ranges.map(range => `• ${range}`).join('\n')}`,
        range: {
            timed: ({ date, start, end }) => `${date}, ${start}–${end}`,
            crossDay: ({ startDate, start, endDate, end }) => `${startDate} ${start}–${endDate} ${end}`,
            allDay: ({ date }) => `${date}, all day`,
            allDayRange: ({ startDate, endDate }) => `${startDate}–${endDate}, all day`,
        },
    },
    fr: {
        timed: ({ date, start, end }) => `Le studio est indisponible le ${date} de ${start} à ${end}.`,
        crossDay: ({ startDate, start, endDate, end }) => `Le studio est indisponible du ${startDate} à ${start} au ${endDate} à ${end}.`,
        allDay: ({ date }) => `Le studio est indisponible toute la journée le ${date}.`,
        allDayRange: ({ startDate, endDate }) => `Le studio est indisponible toute la journée du ${startDate} au ${endDate}.`,
        multiple: (ranges) => `Le studio est indisponible pendant ces créneaux :\n${ranges.map(range => `• ${range}`).join('\n')}`,
        range: {
            timed: ({ date, start, end }) => `${date}, ${start}–${end}`,
            crossDay: ({ startDate, start, endDate, end }) => `${startDate} ${start}–${endDate} ${end}`,
            allDay: ({ date }) => `${date}, toute la journée`,
            allDayRange: ({ startDate, endDate }) => `${startDate}–${endDate}, toute la journée`,
        },
    },
};

/**
 * JavaScript Date를 Paris 타임존의 ISO 문자열로 변환
 * @param {Date} date - 변환할 날짜
 * @returns {string} Paris 타임존 기준 ISO 문자열 (yyyy-MM-dd'T'HH:mm)
 */
export const toParisISOString = (date) => {
    return DateTime.fromJSDate(date).setZone(DEFAULT_TIMEZONE).toFormat("yyyy-MM-dd'T'HH:mm");
};

/**
 * 로컬 입력 문자열을 UTC ISO 문자열로 변환
 * @param {string} localString - 로컬 시간 문자열 (yyyy-MM-dd'T'HH:mm)
 * @param {boolean} isAllDay - 하루종일 이벤트 여부
 * @returns {string} UTC ISO 문자열
 */
export const toUTCISOStringFromLocalInput = (localString, isAllDay = false) => {
    const localDate = DateTime.fromFormat(localString, "yyyy-MM-dd'T'HH:mm", { zone: DEFAULT_TIMEZONE });
    if (isAllDay) {
        return localDate.startOf('day').toUTC().toISO();
    }
    return localDate.toUTC().toISO();
};

/**
 * 현재 캘린더 모드에서 새 일정을 만들 수 있는지 확인합니다.
 * 금지 기간 안내가 필요한 클릭/선택 경로의 단일 기준으로 사용합니다.
 */
export const isCalendarEventCreationMode = (calendarMode) => (
    RESTRICTED_PERIOD_CREATION_MODES.has(calendarMode)
);

/**
 * 날짜 셀이 아니라 FullCalendar 이벤트 요소에서 시작된 클릭인지 확인합니다.
 */
export const isFullCalendarEventElementClick = (jsEvent) => {
    const closest = jsEvent?.target?.closest;

    if (typeof closest !== 'function') {
        return false;
    }

    return Boolean(closest.call(
        jsEvent.target,
        '.fc-event, .fc-bg-event, .restricted-period-event, .blocked-bg-event'
    ));
};

/**
 * FullCalendar 이벤트가 금지 기간인지 확인합니다.
 */
export const isRestrictedPeriodEvent = (event) => Boolean(
    event?.extendedProps?.is_restricted_period || event?.is_restricted_period
);

export const isFullDayRestrictedPeriodRange = (rangeStart, rangeEnd) => {
    const start = DateTime.fromISO(String(rangeStart), { setZone: true }).setZone(DEFAULT_TIMEZONE);
    const end = DateTime.fromISO(String(rangeEnd), { setZone: true }).setZone(DEFAULT_TIMEZONE);

    if (!start.isValid || !end.isValid || start >= end) {
        return false;
    }

    const startsAtMidnight = start.toMillis() === start.startOf('day').toMillis();
    const endsAtMidnight = end.toMillis() === end.startOf('day').toMillis();
    const endsAtLegacyDayEnd = end.hour === 23 && end.minute === 59 && end.second === 59;

    return startsAtMidnight && (endsAtMidnight || endsAtLegacyDayEnd);
};

export const isFullDayRestrictedPeriodEvent = (event) => {
    if (!isRestrictedPeriodEvent(event)) {
        return false;
    }

    if (typeof event?.extendedProps?.isFullDayRestriction === 'boolean') {
        return event.extendedProps.isFullDayRestriction;
    }

    return Boolean(event?.allDay);
};

const toValidTimestamp = (value) => {
    if (!value) {
        return null;
    }

    const timestamp = value instanceof Date
        ? value.getTime()
        : new Date(value).getTime();

    return Number.isFinite(timestamp) ? timestamp : null;
};

const getRestrictedPeriodRange = (event) => {
    if (!isRestrictedPeriodEvent(event)) {
        return null;
    }

    const startValue = event.extendedProps?.restrictedStart ?? event.start;
    const endValue = event.extendedProps?.restrictedEnd ?? event.end;
    const startMs = toValidTimestamp(startValue);
    const endMs = toValidTimestamp(endValue);

    if (startMs === null || endMs === null || startMs >= endMs) {
        return null;
    }

    return {
        event,
        startMs,
        endMs,
        isFullDay: isFullDayRestrictedPeriodEvent(event),
    };
};

const formatRestrictedDate = (dateTime, lang) => {
    if (lang === 'en') {
        return dateTime.setLocale('en').toFormat('MMM d');
    }

    if (lang === 'fr') {
        return dateTime.setLocale('fr').toFormat('d LLL');
    }

    return dateTime.toFormat('M월 d일');
};

const getInclusiveFullDayEnd = (end) => {
    const isLegacyDayEnd = end.hour === 23 && end.minute === 59 && end.second === 59;
    return isLegacyDayEnd ? end : end.minus({ milliseconds: 1 });
};

const buildRestrictedPeriodMessageParts = (period, lang) => {
    const start = DateTime.fromMillis(period.startMs, { zone: 'utc' }).setZone(DEFAULT_TIMEZONE);
    const end = DateTime.fromMillis(period.endMs, { zone: 'utc' }).setZone(DEFAULT_TIMEZONE);

    if (!start.isValid || !end.isValid) {
        return null;
    }

    const startDate = formatRestrictedDate(start, lang);

    if (period.isFullDay) {
        const inclusiveEnd = getInclusiveFullDayEnd(end);
        const endDate = formatRestrictedDate(inclusiveEnd, lang);
        const type = start.hasSame(inclusiveEnd, 'day') ? 'allDay' : 'allDayRange';

        return {
            type,
            values: { date: startDate, startDate, endDate },
        };
    }

    const endDate = formatRestrictedDate(end, lang);
    const values = {
        date: startDate,
        startDate,
        endDate,
        start: start.toFormat('HH:mm'),
        end: end.toFormat('HH:mm'),
    };

    return {
        type: start.hasSame(end, 'day') ? 'timed' : 'crossDay',
        values,
    };
};

/**
 * 선택 구간과 겹치는 금지 이벤트를 실제 시간 순서대로 반환합니다.
 * 중복 로드된 동일 이벤트는 한 번만 포함합니다.
 */
export const findOverlappingRestrictedPeriods = (
    rangeStart,
    rangeEnd,
    events = [],
    { fullDayOnly = false } = {}
) => {
    const rangeStartMs = toValidTimestamp(rangeStart);
    const rangeEndMs = toValidTimestamp(rangeEnd);

    if (
        rangeStartMs === null ||
        rangeEndMs === null ||
        rangeStartMs >= rangeEndMs ||
        !Array.isArray(events)
    ) {
        return [];
    }

    const seen = new Set();

    return events
        .map(getRestrictedPeriodRange)
        .filter(Boolean)
        .filter(period => !fullDayOnly || period.isFullDay)
        .filter(period => rangeStartMs < period.endMs && rangeEndMs > period.startMs)
        .sort((left, right) => left.startMs - right.startMs)
        .filter(period => {
            const eventId = period.event?.id;
            const key = eventId == null
                ? `${period.startMs}:${period.endMs}:${period.isFullDay}`
                : String(eventId);

            if (seen.has(key)) {
                return false;
            }

            seen.add(key);
            return true;
        })
        .map(period => period.event);
};

/**
 * 금지 이벤트의 실제 날짜·시간을 사용자 안내 문구로 변환합니다.
 */
export const formatStudioUnavailableMessage = (events = [], requestedLang = 'ko') => {
    const lang = SUPPORTED_MESSAGE_LANGUAGES.has(requestedLang) ? requestedLang : 'ko';
    const messages = STUDIO_UNAVAILABLE_MESSAGES[lang];
    const periods = Array.isArray(events)
        ? events.map(getRestrictedPeriodRange).filter(Boolean).sort((left, right) => left.startMs - right.startMs)
        : [];
    const parts = periods
        .map(period => buildRestrictedPeriodMessageParts(period, lang))
        .filter(Boolean);

    if (parts.length === 0) {
        return '';
    }

    if (parts.length === 1) {
        const [{ type, values }] = parts;
        return messages[type](values);
    }

    return messages.multiple(parts.map(({ type, values }) => messages.range[type](values)));
};

/**
 * 선택 구간이 금지 기간과 조금이라도 겹치는지 확인합니다.
 * 모든 구간은 종료 시각을 포함하지 않는 [start, end) 규칙을 사용합니다.
 */
export const hasRestrictedPeriodOverlap = (
    rangeStart,
    rangeEnd,
    events = [],
    { fullDayOnly = false } = {}
) => findOverlappingRestrictedPeriods(
    rangeStart,
    rangeEnd,
    events,
    { fullDayOnly }
).length > 0;

export const findBlockingRestrictedPeriods = ({
    calendarMode,
    rangeStart,
    rangeEnd,
    events,
    fullDayOnly = false,
}) => (
    isCalendarEventCreationMode(calendarMode)
        ? findOverlappingRestrictedPeriods(rangeStart, rangeEnd, events, { fullDayOnly })
        : []
);

export const shouldBlockRestrictedPeriodInteraction = ({
    calendarMode,
    rangeStart,
    rangeEnd,
    events,
    fullDayOnly = false,
}) => (
    findBlockingRestrictedPeriods({
        calendarMode,
        rangeStart,
        rangeEnd,
        events,
        fullDayOnly,
    }).length > 0
);

/**
 * API 금지 기간을 FullCalendar 배경 이벤트로 변환합니다.
 * 종일 금지는 날짜 범위로, 시간대 금지는 정확한 시각 범위로 표시합니다.
 */
export const toRestrictedPeriodCalendarEvent = (
    block,
    { interactive = false, label = '' } = {}
) => {
    if (!block?.id || !block?.start_time || !block?.end_time) {
        return null;
    }

    const blockStart = DateTime.fromISO(String(block.start_time), { setZone: true });
    const blockEnd = DateTime.fromISO(String(block.end_time), { setZone: true });

    if (!blockStart.isValid || !blockEnd.isValid || blockStart >= blockEnd) {
        return null;
    }

    const blockStartParis = blockStart.setZone(DEFAULT_TIMEZONE);
    const blockEndParis = blockEnd.setZone(DEFAULT_TIMEZONE);
    const isFullDayRestriction = isFullDayRestrictedPeriodRange(
        blockStart.toISO(),
        blockEnd.toISO()
    );

    const displayStart = isFullDayRestriction
        ? blockStartParis.startOf('day').toISODate()
        : blockStartParis.toISO();
    const displayEnd = isFullDayRestriction
        ? (
            blockEndParis.toMillis() === blockEndParis.startOf('day').toMillis()
                ? blockEndParis.toISODate()
                : blockEndParis.startOf('day').plus({ days: 1 }).toISODate()
        )
        : blockEndParis.toISO();

    return {
        id: `block-${block.id}`,
        title: interactive ? label : '',
        start: displayStart,
        end: displayEnd,
        allDay: isFullDayRestriction,
        display: interactive ? 'block' : 'background',
        backgroundColor: interactive ? 'rgba(190, 32, 48, 0.78)' : 'rgba(255,0,0,0.18)',
        borderColor: interactive ? 'rgba(145, 18, 31, 0.92)' : 'rgba(255,0,0,0.18)',
        className: 'blocked-bg-event',
        editable: false,
        extendedProps: {
            is_restricted_period: true,
            restrictedStart: blockStart.toUTC().toISO(),
            restrictedEnd: blockEnd.toUTC().toISO(),
            isFullDayRestriction,
            reason: block.reason || null,
            createdBy: block.created_by ?? null,
        },
    };
};

/**
 * 백엔드 이벤트 데이터를 FullCalendar 이벤트 형식으로 변환
 * @param {Object} e - 백엔드 이벤트 객체
 * @returns {Object|null} FullCalendar 이벤트 객체 또는 null
 */
export function toCalendarEvent(e) {
    // 기본 유효성 검사
    if (!e || !e.start_time || !e.end_time) {
        return null;
    }

    // ID 유효성 검사
    if (!e.id) {
        return null;
    }

    // Luxon을 사용한 타임존 안전 유효성 검사
    const startDT = DateTime.fromISO(e.start_time, { zone: 'utc' });
    const endDT = DateTime.fromISO(e.end_time, { zone: 'utc' });

    if (!startDT.isValid) {
        return null;
    }

    if (!endDT.isValid) {
        return null;
    }

    if (startDT >= endDT) {
        return null;
    }

    // 색상 유효성 검사
    const safeColor =
        typeof e.user_color === 'string' && /^#[0-9A-F]{6}$/i.test(e.user_color)
            ? e.user_color
            : CALENDAR_DISPLAY.fallbackEventColor;

    // 파리 시간대 기준으로 변환
    const startParis = startDT.setZone(DEFAULT_TIMEZONE);
    const endParis = endDT.setZone(DEFAULT_TIMEZONE);

    let startTime, endTime;

    if (e.is_all_day) {
        // 하루종일 이벤트는 날짜만 사용 (YYYY-MM-DD 형식) - 파리 시간 기준
        startTime = startParis.toFormat('yyyy-MM-dd');
        endTime = endParis.toFormat('yyyy-MM-dd');
    } else {
        // 비-종일 이벤트: 파리 시간대 기준 ISO 문자열로 변환
        startTime = startParis.toISO();
        endTime = endParis.toISO();
    }

    return {
        id: e.id,
        title: e.title,
        start: startTime,
        end: endTime,
        allDay: e.is_all_day,
        display: e.is_all_day ? 'block' : 'auto',
        allDayMaintainDuration: e.is_all_day,
        backgroundColor: safeColor,
        borderColor: safeColor,
        extendedProps: {
            createdBy: e.created_by,
            reservationId: e.id,
            calendarType: e.calendar_type || null,
            calendar_type: e.calendar_type || null,
            originalTitle: e.title,
            userName: e.user_name || null,
            isBidonEvent: e.calendar_type === 'bidon',
            isGuestEvent: e.calendar_type === 'guest',
        },
    };
}

/**
 * 동시 일정 제한 체크 함수 (1분 단위) - 백엔드 API 사용
 * @param {Date|string} newStart - 새 이벤트 시작 시간
 * @param {Date|string} newEnd - 새 이벤트 종료 시간
 * @param {Array} allEvents - 모든 이벤트 배열
 * @param {string|null} excludeId - 제외할 이벤트 ID (수정 시)
 * @param {number} fallbackLimit - API 실패 시 기본 제한 값
 * @param {string} lang - 언어 코드
 * @returns {Promise<Object>} { isOverlapping: boolean, limit?: number, source?: string, description?: string }
 */
export async function isOverlappingLimit(
    newStart,
    newEnd,
    allEvents,
    excludeId = null,
    fallbackLimit = 3,
    lang = 'ko',
    options = DEFAULT_OVERLAP_OPTIONS
) {
    const candidate = excludeId ? allEvents.find(event => String(event.id) === String(excludeId)) : null;
    const result = await apiClient.post('/events/availability', {
        start_time: new Date(newStart).toISOString(),
        end_time: new Date(newEnd).toISOString(),
        exclude_id: excludeId,
        calendar_type: options.calendarType || candidate?.extendedProps?.calendarType || 'studio',
        is_all_day: options.isAllDay ?? candidate?.allDay ?? false,
    });
    const messages = {
        ko: `동시 일정 제한을 초과했습니다. (${result.limit ?? fallbackLimit}개)`,
        en: `Concurrent event limit reached (${result.limit ?? fallbackLimit}).`,
        fr: `Limite d’événements simultanés atteinte (${result.limit ?? fallbackLimit}).`,
    };
    return { isOverlapping: !result.allowed, ...result, description: result.code === 'CALENDAR_CAPACITY' ? messages[lang] || messages.en : result.message };

}

/**
 * 색상 코드 유효성 검사
 * @param {string} color - 검사할 색상 코드
 * @returns {boolean} 유효한 HEX 색상 코드인지 여부
 */
export const isValidHexColor = (color) => {
    return typeof color === 'string' && /^#[0-9A-F]{6}$/i.test(color);
};

/**
 * 기본 이벤트 색상
 */
export const DEFAULT_EVENT_COLOR = '#00cc00';
